import PropTypes from 'prop-types';
import { Plus, Trash2, Zap } from 'lucide-react';
import { STAT_CARD_BASE } from '../../ui/StatCard';
import { KPI_KEYS } from '../../../utils/simulationEngine';
import { EditorHeading, Field, TextArea, TextInput } from './EditorFields';
import MetricDeltas from './MetricDeltas';

const blankEvent = (order) => ({
  id: `evt_custom_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
  order,
  title: '',
  description: '',
  effectOnTeams: '',
  kpi: KPI_KEYS.reduce((acc, k) => { acc[k] = 0; return acc; }, {}),
});

/**
 * Surprise events: pressures from outside the room that land on every team at once.
 * The trainer chooses one per round — or none — from the console while teams decide.
 */
const EventsEditor = ({ label, events, kpis, onChange }) => {
  const set = (i, patch) => onChange(events.map((e, x) => (x === i ? { ...e, ...patch } : e)));

  return (
    <div className={`${STAT_CARD_BASE} p-5`}>
      <EditorHeading
        title={(
          <span className="flex items-center gap-2">
            <Zap size={15} strokeWidth={1.75} className="text-amber-500" />
            {label}
          </span>
        )}
      >
        Optional. While teams decide, you can play one of these — or nothing — and it lands on
        every team at the reveal.
      </EditorHeading>

      <div className="space-y-3">
        {events.map((e, i) => (
          <div key={e.id} className="rounded-xl border border-neutral-200 p-3.5 dark:border-neutral-700/60">
            <div className="flex items-start gap-2.5">
              <div className="min-w-0 flex-1 space-y-2.5">
                <Field label="Event">
                  <TextInput value={e.title} onChange={(v) => set(i, { title: v })} />
                </Field>
                <Field label="What happens">
                  <TextArea value={e.description} onChange={(v) => set(i, { description: v })} rows={2} />
                </Field>
                <Field label="What it does to the room">
                  <TextInput value={e.effectOnTeams} onChange={(v) => set(i, { effectOnTeams: v })} />
                </Field>
                <MetricDeltas kpis={kpis} value={e.kpi} onChange={(kpi) => set(i, { kpi })} />
              </div>
              <button
                type="button"
                onClick={() => onChange(events.filter((_, x) => x !== i))}
                className="mt-6 shrink-0 rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-red-600 dark:hover:bg-neutral-800"
                aria-label="Remove this event"
              >
                <Trash2 size={15} strokeWidth={2} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onChange([...events, blankEvent(events.length + 1)])}
        className="mt-3 w-full rounded-xl border-2 border-dashed border-neutral-300 py-2.5 text-xs font-medium text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 dark:border-neutral-700 dark:hover:border-neutral-600"
      >
        <Plus size={14} strokeWidth={2} className="mr-1 inline" />
        Add an event
      </button>
    </div>
  );
};

EventsEditor.propTypes = {
  label: PropTypes.node.isRequired,
  events: PropTypes.arrayOf(PropTypes.object).isRequired,
  kpis: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default EventsEditor;
